import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminDataQualityApi, orderApi, opsApi, downloadAuthed } from '../api/services';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle, HelpCircle, Link2, Package, Truck, Pause, Inbox, XCircle,
  Download, Clock, CheckSquare, FileWarning,
} from 'lucide-react';

function StatCard({ icon: Icon, label, value, tone = 'default', to }) {
  const palette = {
    default: 'bg-white text-gray-900',
    warn: 'bg-amber-50 text-amber-900',
    danger: 'bg-red-50 text-red-900',
    info: 'bg-blue-50 text-blue-900',
  }[tone];
  const body = (
    <div className={`p-4 rounded-xl border border-gray-200 ${palette}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase font-semibold tracking-wide opacity-70">{label}</div>
          <div className="text-2xl font-bold mt-1">{value ?? '—'}</div>
        </div>
        <Icon className="w-8 h-8 opacity-50" />
      </div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export default function Dashboard() {
  const { user, isAdmin, isOps, isDispatchUser } = useAuth();
  const role = isAdmin() ? 'admin' : isOps() ? 'ops' : isDispatchUser() ? 'dispatch_user' : 'other';

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">
            Welcome back, {user?.first_name || user?.firstName || user?.email}. Roles: {user?.roles?.join(', ')}
          </p>
        </div>
        {isAdmin() && (
          <button
            onClick={() => downloadAuthed('/orders/export.csv', 'orders.csv').catch(() =>
              toast.error('Download failed'))}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
            <Download className="w-4 h-4" /> Orders CSV
          </button>
        )}
      </div>

      {/* D1 (Kalpana UAT round 3): all roles see the comprehensive 'full
          dashboard overview' (Needs attention + Data quality + Pipeline)
          first, then their role-specific queue underneath. Replaces the
          previous role-branched dashboards. Ops + Dispatch User were
          getting a stripped-down view before. */}
      <AdminDashboard />

      {/* Role-specific section below the shared overview. */}
      {role === 'ops' && (
        <div className="mt-6"><OpsDashboard /></div>
      )}
      {role === 'dispatch_user' && (
        <div className="mt-6"><DispatchUserDashboard user={user} /></div>
      )}
    </div>
  );
}

// ---- Admin ------------------------------------------------------------

function AdminDashboard() {
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAdmin, isOps } = useAuth();
  useEffect(() => {
    adminDataQualityApi.summary()
      .then((res) => setCounts(res.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-gray-500">Loading…</div>;
  if (!counts) return null;

  // K1 (Kalpana UAT round 4): every dashboard tile is clickable for
  // every authenticated role. Where a role can't access an admin-only
  // destination, we route them to the closest equivalent that they CAN
  // access (e.g. dispatch_user clicking 'Reships need linking' goes to
  // /intake?filter=reship_unlinked which they can read). Falls back to
  // global Search if no role-specific landing makes sense.
  const route = (paths) =>
    isAdmin() ? paths.admin : isOps() ? (paths.ops || paths.fallback) : (paths.user || paths.fallback);

  return (
    <>
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Needs attention</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <StatCard icon={AlertTriangle} tone="danger"
                  label="Blocked (troublesome)" value={counts.blocked_troublesome}
                  to={route({ admin: '/admin/data-quality?tab=blocked',
                              ops:   '/search?stage=blocked_troublesome_customer',
                              user:  '/search?stage=blocked_troublesome_customer',
                              fallback: '/' })} />
        <StatCard icon={HelpCircle} tone="warn"
                  label="Virtual products" value={counts.data_incomplete}
                  to={route({ admin: '/admin/data-quality?tab=virtual',
                              ops:   '/ops',
                              user:  '/intake',
                              fallback: '/' })} />
        <StatCard icon={Link2} tone="warn"
                  label="Reships need linking" value={counts.reship_unlinked}
                  to={route({ admin: '/intake?filter=reship_unlinked',
                              ops:   '/intake?filter=reship_unlinked',
                              user:  '/intake?filter=reship_unlinked',
                              fallback: '/intake' })} />
      </div>

      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Data quality</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <StatCard icon={AlertTriangle} label="Missing zipcodes (intake)" value={counts.missing_zip}
                  to={route({ admin: '/admin/data-quality?flag=missing_zip',
                              user:  '/intake',
                              ops:   '/ops',
                              fallback: '/intake' })} />
        <StatCard icon={AlertTriangle} label="Special-char addresses" value={counts.special_char_address}
                  to={route({ admin: '/admin/data-quality?flag=special_char',
                              user:  '/intake',
                              ops:   '/ops',
                              fallback: '/intake' })} />
      </div>

      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Pipeline</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Inbox}  label="Ops queue"   value={counts.ops_pending}
                  to={route({ admin: '/ops', ops: '/ops', user: '/my-queue', fallback: '/' })} />
        <StatCard icon={Pause}  label="On hold"     value={counts.hold_active}
                  to={route({ admin: '/hold-orders', ops: '/hold-orders',
                              user: '/hold-orders', fallback: '/hold-orders' })} />
        <StatCard icon={Truck}  label="In transit"  value={counts.in_transit}
                  to={route({ admin: '/pending-tracking', ops: '/pending-tracking',
                              user: '/pending-tracking', fallback: '/pending-tracking' })} />
        <StatCard icon={Package} label="Intake"
                  value={(counts.blocked_troublesome || 0) + (counts.data_incomplete || 0) + (counts.reship_unlinked || 0)}
                  to="/intake" />
      </div>
    </>
  );
}

// ---- Ops --------------------------------------------------------------

function OpsDashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    opsApi.queue({})
      .then((res) => setRows(res.data.rows || []))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load queue'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-gray-500">Loading…</div>;

  // Group by category (from the _category hint the backend already computes).
  const byCategory = rows.reduce((acc, r) => {
    const k = r._category || 'other';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const catLabel = {
    cancellation:           'Cancellations',
    hold:                   'Holds',
    vendor_issue:           'Vendor issues',
    customer_approval:      'Customer approval',
    address_verification:   'Address verify',
    data_incomplete:        'Data incomplete',
    tracking:               'Tracking',
    manual_review:          'Manual review',
    other:                  'Other',
  };

  return (
    <>
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">My queue</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard icon={Inbox} tone="info" label="Total in queue" value={rows.length} to="/ops" />
        {Object.entries(byCategory).map(([cat, n]) => (
          <StatCard key={cat} icon={AlertTriangle}
                    label={catLabel[cat] || cat} value={n} to={`/ops`} />
        ))}
      </div>

      {rows.length > 0 && (
        <>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Oldest open</h2>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">Order</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Reason</th>
                  <th className="text-left px-3 py-2">Waiting</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.slice(0, 10).map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link to={`/orders/${r.id}`} className="text-blue-600 hover:underline">
                        {r.order_id_external}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{r.customer_name}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.reason_label || r.current_owner_reason_code}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {r.current_owner_since ? formatDistanceToNow(new Date(r.current_owner_since), { addSuffix: true }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

// ---- Dispatch User ----------------------------------------------------

function DispatchUserDashboard({ user }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    orderApi.listByOwner({ team: 'dispatch_user', userId: user?.id })
      .then((res) => setRows(res.data.orders || []))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [user?.id]);

  if (loading) return <div className="text-sm text-gray-500">Loading…</div>;

  const byStage = rows.reduce((acc, r) => {
    acc[r.lifecycle_stage] = (acc[r.lifecycle_stage] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">My orders</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard icon={CheckSquare} tone="info" label="Total assigned" value={rows.length} to="/intake" />
        <StatCard icon={HelpCircle} tone="warn" label="In review" value={byStage.in_review || 0} />
        <StatCard icon={Clock}       label="Intake"    value={byStage.intake || 0} />
        <StatCard icon={FileWarning} label="Data incomplete" value={byStage.data_incomplete || 0} />
      </div>

      {rows.length > 0 && (
        <>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Oldest assigned</h2>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">Order</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Stage</th>
                  <th className="text-left px-3 py-2">Assigned</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.slice(0, 10).map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link to={`/orders/${r.id}`} className="text-blue-600 hover:underline">
                        {r.order_id_external}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{r.customer_name}</td>
                    <td className="px-3 py-2 text-xs capitalize">{r.lifecycle_stage?.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {r.current_owner_since ? formatDistanceToNow(new Date(r.current_owner_since), { addSuffix: true }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
